import type { ReactNode } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { StatusPill } from '../shared/pills'
import { useAdminOverview } from './admin-overview-provider'
import { deriveTrackHealth } from './derive'

export function TrackHealth(): ReactNode {
  const { overview, isLoading } = useAdminOverview()
  const rows = deriveTrackHealth(overview?.completionByTrack ?? [])

  return (
    <Card className="h-full gap-4 border-(--cd-border) bg-(--cd-card) py-4 text-(--cd-text) shadow-none">
      <CardHeader className="gap-1 px-4">
        <CardTitle className="text-sm">Track health</CardTitle>
        <CardDescription className="text-xs text-(--cd-text-muted)">
          Where they thrive and struggle
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-2.5 px-4">
        {isLoading ? (
          <p className="py-4 text-center text-xs text-(--cd-text-faint)">
            Loading track data…
          </p>
        ) : rows.length === 0 ? (
          <p className="py-4 text-center text-xs text-(--cd-text-faint)">
            No track data yet — it appears once homework is scheduled.
          </p>
        ) : (
          rows.map((row) => {
            const healthy = row.status === 'healthy'

            return (
              <Card
                key={row.track}
                className="gap-2 border-(--cd-border-2) bg-(--cd-inset) px-3 py-3 text-(--cd-text) shadow-none"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    {row.trend ? (
                      <span
                        aria-hidden="true"
                        className={
                          healthy ? 'text-(--cd-green)' : 'text-(--cd-red)'
                        }
                      >
                        {row.trend === 'up' ? '↗' : '↘'}
                      </span>
                    ) : null}
                    <span className="text-xs font-semibold">{row.label}</span>
                  </div>
                  <StatusPill tone={healthy ? 'green' : 'red'}>
                    {healthy ? 'Healthy' : 'Struggling'}
                  </StatusPill>
                </div>
                <span className="text-2xl font-bold tracking-tight">
                  {row.percent}%
                </span>
                <p className="text-xs leading-5 text-(--cd-text-muted)">
                  {row.completed} of {row.scheduled} assigned{' '}
                  {row.scheduled === 1 ? 'task' : 'tasks'} completed
                </p>
              </Card>
            )
          })
        )}
      </CardContent>
    </Card>
  )
}
